import { Component, ViewChildren, QueryList, OnInit, ElementRef, AfterViewInit, Input, ChangeDetectorRef } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { PdfViewerService, Annotation } from 'src/app/services/pdf-viewer.service';

@Component({
  selector: 'app-setup',
  templateUrl: './setup.page.html',
  styleUrls: ['./setup.page.scss'],
})
export class SetupPage implements OnInit, AfterViewInit {

  pdfDoc: any = null;
  pageNumbers: number[] = [];
  scale: number = 1;
  currentPage: number = 1;

  annotations: Annotation[] = [];
  selectedAnnotation: Annotation | null = null;
  isDragging = false;
  isResizing = false;
  resizeHandle: string = ''; // 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
  dragStartX = 0;
  dragStartY = 0;
  resizeStartWidth = 0;
  resizeStartHeight = 0;
  resizeStartX = 0;
  resizeStartY = 0;

  // Resize handle size in pixels
  readonly HANDLE_SIZE = 10;

  page_annot_data = [];
  page_form_data = [];
  current_file_guid = "";
  files: any;
  current_file_index = 0;
  current_selected_input: any = null;
  removed_empty_forms = false;
  esignature_templates: any = null;
  current_template: any = null;

  @Input() prepare_files = true;
  @Input() client_guid = '';
  @Input() company_guid = '';
  @Input() role_guid = '';
  @Input() use_cert = true;

  recipient_name = 'Display Name';
  recipient_email = 'User Email';

  setup_files: any = [];

  // Image upload state
  showImageUploadModal = false;
  uploadedImageData: string | null = null;
  uploadedImageName: string = '';

  // Text input state
  showTextInputModal = false;
  textInputValue: string = '';
  textInputFontSize: string = '14pt';
  textInputColor: string = '#000000';

  // Canvas references
  @ViewChildren('pdfCanvas') pdfCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('annotationCanvas') annotationCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;

  constructor(
    private apiService: ApiService,
    public globalService: GlobalService,
    private modalController: ModalController,
    public pdfViewerService: PdfViewerService,
    private cdr: ChangeDetectorRef,
    private alertController: AlertController
  ) {
    this.GetEsignatureTemplates();
  }

  async ngOnInit() {
    await this.ResolveRecipient();
  }

  private async ResolveRecipient(): Promise<void> {
    if (this.company_guid) {
      this.recipient_name = "Company Name";
      this.recipient_email = "Default Contact Email";
    }
  }

  ngAfterViewInit(): void {
    if (this.files && this.files.length > 0) {
      this.LoadDocument(this.files[this.current_file_index]);
    }
  }

  async LoadDocument(file: any) {
    try {
      this.current_file_guid = file.guid;
      let OTC: any = await this.apiService.get("one_time_code");
      OTC = OTC.otc;
      const customHeaders = Object();
      customHeaders["OTC"] = OTC;

      const url = this.apiService.GetDownloadURL(file);

      // Load PDF using PDF.js
      this.pdfDoc = await this.pdfViewerService.loadPDF(url, customHeaders);

      this.pageNumbers = [];
      for (let i = 1; i <= this.pdfViewerService.getNumPages(); i++) {
        this.pageNumbers.push(i);
      }

      // Clear existing annotations when loading new document
      this.annotations = [];
      this.selectedAnnotation = null;

      // Wait for view to update with canvas elements
      setTimeout(() => {
        this.RenderAllPages();
      }, 100);
    } catch (error) {
      console.error('Error loading document:', error);
      this.globalService.presentToastMessage('Error loading PDF', false);
    }
  }

  async RenderAllPages(): Promise<void> {
    if (!this.pdfViewerService.getNumPages()) return;

    for (let i = 1; i <= this.pdfViewerService.getNumPages(); i++) {
      await this.RenderPage(i);
    }
  }

  async RenderPage(pageNum: number): Promise<void> {
    const pdfCanvas = this.pdfCanvases.toArray().find(c =>
      c.nativeElement.getAttribute('data-page') == pageNum.toString()
    );
    const annotCanvas = this.annotationCanvases.toArray().find(c =>
      c.nativeElement.getAttribute('data-page') == pageNum.toString()
    );

    if (!pdfCanvas || !annotCanvas) return;

    await this.pdfViewerService.renderPage(
      pageNum,
      pdfCanvas.nativeElement,
      this.scale
    );

    // Match annotation canvas size to PDF canvas
    annotCanvas.nativeElement.width = pdfCanvas.nativeElement.width;
    annotCanvas.nativeElement.height = pdfCanvas.nativeElement.height;

    this.RedrawAnnotations(pageNum);
  }

  RedrawAnnotations(pageNum?: number): void {
    const pagesToRedraw = pageNum ? [pageNum] : this.pageNumbers;

    pagesToRedraw.forEach(page => {
      const annotationCanvas = this.annotationCanvases.toArray().find(
        (c: ElementRef) => parseInt(c.nativeElement.getAttribute('data-page') || '0') === page
      );

      if (!annotationCanvas) return;

      const ctx = annotationCanvas.nativeElement.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, annotationCanvas.nativeElement.width, annotationCanvas.nativeElement.height);

      const pageAnnotations = this.annotations.filter(a => a.page_num === page);

      pageAnnotations.forEach(annotation => {
        this.pdfViewerService.drawAnnotation(
          ctx,
          annotation,
          this.selectedAnnotation?.id === annotation.id
        );
      });
    });
  }

  private GetVisiblePageCenter(): { pageNum: number; centerX: number; centerY: number } {
    const container = document.querySelector('.pdf-viewer-container') as HTMLElement;
    if (!container) {
      return { pageNum: this.currentPage || 1, centerX: 0, centerY: 0 };
    }

    const containerRect = container.getBoundingClientRect();
    let bestPage = this.currentPage || 1;
    let bestCanvas: HTMLCanvasElement | null = null;
    let bestOverlap = -Infinity;

    this.annotationCanvases.toArray().forEach((c: ElementRef<HTMLCanvasElement>) => {
      const pageNum = parseInt(c.nativeElement.getAttribute('data-page') || '0');
      const rect = c.nativeElement.getBoundingClientRect();
      const overlap = Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestPage = pageNum;
        bestCanvas = c.nativeElement;
      }
    });

    if (!bestCanvas) {
      return { pageNum: bestPage, centerX: 0, centerY: 0 };
    }

    const canvasRect = (bestCanvas as HTMLCanvasElement).getBoundingClientRect();
    const scaleY = (bestCanvas as HTMLCanvasElement).height / canvasRect.height;

    // Center within the visible portion of the canvas, not the full canvas
    const visibleTop    = Math.max(containerRect.top, canvasRect.top);
    const visibleBottom = Math.min(containerRect.bottom, canvasRect.bottom);
    const centerX = (bestCanvas as HTMLCanvasElement).width / 2;
    const centerY = ((visibleTop + visibleBottom) / 2 - canvasRect.top) * scaleY;

    this.currentPage = bestPage;
    return { pageNum: bestPage, centerX, centerY };
  }

  AddAnnotation(
    text: string,
    type: string,
    backgroundColor: string,
    textColor: string,
    width: number,
    height: number,
    fontSize: string
  ): void {
    if (!this.pdfDoc) return;

    const { pageNum, centerX, centerY } = this.GetVisiblePageCenter();

    const annotation: Annotation = {
      id: this.pdfViewerService.generateAnnotationId(),
      tool_name: type as any,
      x: centerX - (width * this.scale / 2),
      y: centerY - (height * this.scale / 2),
      width: width * this.scale,
      height: height * this.scale,
      page_num: pageNum,
      value: text,
      fontSize: fontSize,
      backgroundColor: backgroundColor,
      text_colour: textColor,
      rotation: 0
    };

    this.annotations.push(annotation);
    this.selectedAnnotation = annotation;
    this.RedrawAnnotations(pageNum);
  }

  OnCanvasMouseDown(event: MouseEvent, pageNum: number): void {
    const canvas = event.target as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    // Update current page
    this.currentPage = pageNum;

    // Check if clicking on a resize handle of the selected annotation
    if (this.selectedAnnotation && this.selectedAnnotation.page_num === pageNum) {
      const handle = this.GetResizeHandle(x, y, this.selectedAnnotation);
      if (handle) {
        this.isResizing = true;
        this.resizeHandle = handle;
        this.dragStartX = x;
        this.dragStartY = y;
        this.resizeStartWidth = this.selectedAnnotation.width;
        this.resizeStartHeight = this.selectedAnnotation.height;
        this.resizeStartX = this.selectedAnnotation.x;
        this.resizeStartY = this.selectedAnnotation.y;
        return;
      }
    }

    // Check if clicking on an existing annotation
    const pageAnnotations = this.annotations.filter(a => a.page_num === pageNum);

    // Check in reverse order (top annotations first)
    for (let i = pageAnnotations.length - 1; i >= 0; i--) {
      const annotation = pageAnnotations[i];

      if (
        x >= annotation.x &&
        x <= annotation.x + annotation.width &&
        y >= annotation.y &&
        y <= annotation.y + annotation.height
      ) {
        this.selectedAnnotation = annotation;
        this.isDragging = true;
        this.dragStartX = x;
        this.dragStartY = y;
        this.RedrawAnnotations(pageNum);
        return;
      }
    }

    // No annotation clicked
    this.selectedAnnotation = null;
    this.RedrawAnnotations(pageNum);
  }

  OnCanvasMouseMove(event: MouseEvent, pageNum: number): void {
    const canvas = event.target as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    // Handle resizing
    if (this.isResizing && this.selectedAnnotation) {
      this.HandleResize(x, y, pageNum);
      return;
    }

    // Handle dragging
    if (this.isDragging && this.selectedAnnotation) {
      const dx = x - this.dragStartX;
      const dy = y - this.dragStartY;

      this.selectedAnnotation.x += dx;
      this.selectedAnnotation.y += dy;

      this.dragStartX = x;
      this.dragStartY = y;

      this.RedrawAnnotations(pageNum);
      return;
    }

    // Update cursor based on position
    this.UpdateCursor(canvas, x, y, pageNum);
  }

  OnCanvasMouseUp(_event: MouseEvent, _pageNum: number): void {
    this.isDragging = false;
    this.isResizing = false;
    this.resizeHandle = '';
  }

  OnCanvasMouseLeave(event: MouseEvent, pageNum: number): void {
    const canvas = event.target as HTMLCanvasElement;
    canvas.style.cursor = 'default';

    // Don't reset dragging/resizing state - user might move mouse fast
  }

  // Get which resize handle is at the given position
  GetResizeHandle(x: number, y: number, annotation: Annotation): string {
    const hs = this.HANDLE_SIZE;
    const ax = annotation.x;
    const ay = annotation.y;
    const aw = annotation.width;
    const ah = annotation.height;

    // Corner handles
    if (x >= ax - hs && x <= ax + hs && y >= ay - hs && y <= ay + hs) return 'nw';
    if (x >= ax + aw - hs && x <= ax + aw + hs && y >= ay - hs && y <= ay + hs) return 'ne';
    if (x >= ax - hs && x <= ax + hs && y >= ay + ah - hs && y <= ay + ah + hs) return 'sw';
    if (x >= ax + aw - hs && x <= ax + aw + hs && y >= ay + ah - hs && y <= ay + ah + hs) return 'se';

    // Edge handles
    if (x >= ax + aw/2 - hs && x <= ax + aw/2 + hs && y >= ay - hs && y <= ay + hs) return 'n';
    if (x >= ax + aw/2 - hs && x <= ax + aw/2 + hs && y >= ay + ah - hs && y <= ay + ah + hs) return 's';
    if (x >= ax - hs && x <= ax + hs && y >= ay + ah/2 - hs && y <= ay + ah/2 + hs) return 'w';
    if (x >= ax + aw - hs && x <= ax + aw + hs && y >= ay + ah/2 - hs && y <= ay + ah/2 + hs) return 'e';

    return '';
  }

  // Handle resize operation
  HandleResize(x: number, y: number, pageNum: number): void {
    if (!this.selectedAnnotation) return;

    const dx = x - this.dragStartX;
    const dy = y - this.dragStartY;
    const minSize = 20; // Minimum annotation size

    switch (this.resizeHandle) {
      case 'se': // Bottom-right
        this.selectedAnnotation.width = Math.max(minSize, this.resizeStartWidth + dx);
        this.selectedAnnotation.height = Math.max(minSize, this.resizeStartHeight + dy);
        break;
      case 'sw': // Bottom-left
        this.selectedAnnotation.width = Math.max(minSize, this.resizeStartWidth - dx);
        this.selectedAnnotation.height = Math.max(minSize, this.resizeStartHeight + dy);
        this.selectedAnnotation.x = this.resizeStartX + (this.resizeStartWidth - this.selectedAnnotation.width);
        break;
      case 'ne': // Top-right
        this.selectedAnnotation.width = Math.max(minSize, this.resizeStartWidth + dx);
        this.selectedAnnotation.height = Math.max(minSize, this.resizeStartHeight - dy);
        this.selectedAnnotation.y = this.resizeStartY + (this.resizeStartHeight - this.selectedAnnotation.height);
        break;
      case 'nw': // Top-left
        this.selectedAnnotation.width = Math.max(minSize, this.resizeStartWidth - dx);
        this.selectedAnnotation.height = Math.max(minSize, this.resizeStartHeight - dy);
        this.selectedAnnotation.x = this.resizeStartX + (this.resizeStartWidth - this.selectedAnnotation.width);
        this.selectedAnnotation.y = this.resizeStartY + (this.resizeStartHeight - this.selectedAnnotation.height);
        break;
      case 'n': // Top
        this.selectedAnnotation.height = Math.max(minSize, this.resizeStartHeight - dy);
        this.selectedAnnotation.y = this.resizeStartY + (this.resizeStartHeight - this.selectedAnnotation.height);
        break;
      case 's': // Bottom
        this.selectedAnnotation.height = Math.max(minSize, this.resizeStartHeight + dy);
        break;
      case 'e': // Right
        this.selectedAnnotation.width = Math.max(minSize, this.resizeStartWidth + dx);
        break;
      case 'w': // Left
        this.selectedAnnotation.width = Math.max(minSize, this.resizeStartWidth - dx);
        this.selectedAnnotation.x = this.resizeStartX + (this.resizeStartWidth - this.selectedAnnotation.width);
        break;
    }

    this.RedrawAnnotations(pageNum);
  }

  // Update cursor based on mouse position
  UpdateCursor(canvas: HTMLCanvasElement, x: number, y: number, pageNum: number): void {
    // Check resize handles on selected annotation
    if (this.selectedAnnotation && this.selectedAnnotation.page_num === pageNum) {
      const handle = this.GetResizeHandle(x, y, this.selectedAnnotation);
      if (handle) {
        canvas.style.cursor = this.GetCursorForHandle(handle);
        return;
      }

      // Check if over the selected annotation (move cursor)
      if (
        x >= this.selectedAnnotation.x &&
        x <= this.selectedAnnotation.x + this.selectedAnnotation.width &&
        y >= this.selectedAnnotation.y &&
        y <= this.selectedAnnotation.y + this.selectedAnnotation.height
      ) {
        canvas.style.cursor = 'move';
        return;
      }
    }

    // Check if over any annotation
    const pageAnnotations = this.annotations.filter(a => a.page_num === pageNum);
    for (let i = pageAnnotations.length - 1; i >= 0; i--) {
      const annotation = pageAnnotations[i];
      if (
        x >= annotation.x &&
        x <= annotation.x + annotation.width &&
        y >= annotation.y &&
        y <= annotation.y + annotation.height
      ) {
        canvas.style.cursor = 'pointer';
        return;
      }
    }

    canvas.style.cursor = 'default';
  }

  // Get cursor style for resize handle
  GetCursorForHandle(handle: string): string {
    switch (handle) {
      case 'nw':
      case 'se':
        return 'nwse-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      default:
        return 'default';
    }
  }

  DeleteSelectedInput(): void {
    if (!this.selectedAnnotation) return;

    const index = this.annotations.findIndex(a => a.id === this.selectedAnnotation!.id);
    if (index > -1) {
      const pageNum = this.annotations[index].page_num;
      this.annotations.splice(index, 1);
      this.selectedAnnotation = null;
      this.RedrawAnnotations(pageNum);
    }
  }

  ZoomIn(): void {
    this.scale = Math.min(this.scale + 0.25, 3.0);
    this.RescaleAnnotations(this.scale / (this.scale - 0.25));
    this.RenderAllPages();
  }

  ZoomOut(): void {
    const oldScale = this.scale;
    this.scale = Math.max(this.scale - 0.25, 0.5);
    this.RescaleAnnotations(this.scale / oldScale);
    this.RenderAllPages();
  }

  ResetZoom(): void {
    const oldScale = this.scale;
    this.scale = 1;
    this.RescaleAnnotations(this.scale / oldScale);
    this.RenderAllPages();
  }

  async AskToRemoveEmpty(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Remove Empty Annotations',
      message: 'Are you sure you would like to remove all empty annotations?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Remove', handler: () => { this.RemoveEmptyAnnotations(); } }
      ]
    });
    await alert.present();
  }

  async AskToRemoveAll(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Remove All Annotations',
      message: 'Are you sure you would like to remove all annotations?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Remove', handler: () => { this.RemoveAllAnnotations(); } }
      ]
    });
    await alert.present();
  }

  RemoveEmptyAnnotations(): void {
    const pagesToRedraw = new Set<number>();

    this.annotations = this.annotations.filter(annotation => {
      if (!annotation.value || annotation.value.trim() === '') {
        pagesToRedraw.add(annotation.page_num);
        return false;
      }
      return true;
    });

    pagesToRedraw.forEach(pageNum => this.RedrawAnnotations(pageNum));
  }

  RemoveAllAnnotations(): void {
    const pagesToRedraw = new Set(this.annotations.map(a => a.page_num));
    this.annotations = [];
    this.selectedAnnotation = null;
    pagesToRedraw.forEach(pageNum => this.RedrawAnnotations(pageNum));
  }

  async SaveTemplate(): Promise<void> {
    const name = window.prompt("Please enter a template name.", "");
    if (!name || name.trim() === '') return;

    const templateData = {
      title: name,
      data: JSON.stringify(this.annotations)
    };
    this.globalService.presentLoading('Saving Template...', 99999);
    const data: any = await this.apiService.post('esignatures/templates', templateData);
    this.globalService.dismissLoading();
    this.globalService.presentToastMessage(data.message, data.success);

    if (data.success) {
      this.current_template = data.data[0];
      if (!this.esignature_templates) {
        this.esignature_templates = [];
      }
      this.esignature_templates.push(this.current_template);
    }
  }

  async GetEsignatureTemplates(): Promise<void> {
    const data: any = await this.apiService.get('esignatures/templates');
    if (data.success) {
      this.esignature_templates = data.data;
    }
  }

  async LoadTemplate(guid: string): Promise<void> {
    const data: any = await this.apiService.get(`esignatures/templates/${guid}`);

    if (data.success) {
      this.RemoveAllAnnotations();
      this.current_template = data.data[0];

      try {
        const templateAnnotations = JSON.parse(this.current_template.data);

        templateAnnotations.forEach((annot: any) => {
          if (annot.pageNumber <= this.pdfDoc.numPages) {
            this.annotations.push({
              id: this.pdfViewerService.generateAnnotationId(),
              tool_name: annot.tool_name || annot.tool_name?.replace(/[{}]/g, ''),
              x: annot.x,
              y: annot.y,
              width: annot.width,
              height: annot.height,
              page_num: annot.page_num || annot.page_num,
              value: annot.value,
              fontSize: annot.fontSize || annot.font_size,
              backgroundColor: annot.backgroundColor || annot.fill_colour || '#FFFFFF',
              text_colour: annot.text_colour || annot.text_colour || '#008000',
              rotation: annot.rotation || 0
            });
          }
        });

        this.pageNumbers.forEach(pageNum => this.RedrawAnnotations(pageNum));
      } catch (e) {
        console.error('Error loading template:', e);
      }
    }
  }

  async UpdateTemplate(guid: string): Promise<void> {
    const templateData = {
      data: JSON.stringify(this.annotations)
    };
    this.globalService.presentLoading('Updating Template...', 99999);
    const data: any = await this.apiService.put(`esignatures/templates/${guid}`, templateData);
    this.globalService.dismissLoading();
    this.globalService.presentToastMessage(data.message, data.success);
  }

  async DeleteTemplate(guid: string): Promise<void> {
    this.globalService.presentLoading('Deleting Template...', 99999);
    const data: any = await this.apiService.delete(`esignatures/templates/${guid}`, null);
    this.globalService.dismissLoading();
    this.globalService.presentToastMessage(data.message, data.success);

    this.current_template = null;
    this.globalService.RemoveFromArray(this.esignature_templates, guid);
  }

  CheckForSignatureFields(): boolean {
    return this.annotations.some(a => a.tool_name === 'signature');
  }

  async PrepareForSigning(): Promise<void> {
    if (!this.CheckForSignatureFields()) {
      const alert = await this.alertController.create({
        header: 'Confirm Signature Request',
        message: 'Are you sure you are finished creating the signature request? It currently does not have any signature fields.',
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Confirm',
            handler: async () => { await this.ExecutePrepareForSigning(); }
          }
        ]
      });
      await alert.present();
      return;
    }

    await this.ExecutePrepareForSigning();
  }

  private async ExecutePrepareForSigning(): Promise<void> {
    const annotationData = await this.pdfViewerService.exportAnnotationsToPDF(this.annotations, this.scale);

    if (this.prepare_files) {
      const postData = {
        client_guid: this.client_guid || '',
        company_guid: this.company_guid || '',
        role_guid: this.role_guid || '',
        xfdf_data: annotationData,
        page_annot_data: annotationData,
        page_form_data: JSON.stringify([])
      };

      this.globalService.presentLoading('Preparing For Signing', 99999);
      const data: any = await this.apiService.post(`esignature/prepare/${this.current_file_guid}`, postData);
      this.globalService.presentToastMessage(data.message, data.success);

      if (data.success) {
        this.globalService.dismissLoading();
        this.setup_files.push({ file_guid: this.current_file_guid });
        this.current_file_index++;

        if (this.current_file_index >= this.files.length) {
          this.modalController.dismiss(this.setup_files);
        } else {
          await this.LoadDocument(this.files[this.current_file_index]);
        }
      }
    } else {
      const returnData = {
        file_guid: this.current_file_guid,
        xfdf_data: annotationData,
        page_annot_data: annotationData,
        page_form_data: JSON.stringify([])
      };

      this.setup_files.push(returnData);
      this.current_file_index++;

      if (this.current_file_index >= this.files.length) {
        this.modalController.dismiss(this.setup_files);
      } else {
        await this.LoadDocument(this.files[this.current_file_index]);
      }
    }
  }

  SkipThisFile(): void {
    this.current_file_index++;
    if (this.current_file_index >= this.files.length) {
      this.modalController.dismiss(this.setup_files);
    } else {
      this.LoadDocument(this.files[this.current_file_index]);
    }
  }

  Close(): void {
    this.modalController.dismiss(false);
  }

  // Image Upload Methods
  OpenImageUploadModal(): void {
    this.showImageUploadModal = true;
    this.uploadedImageData = null;
    this.uploadedImageName = '';
    this.cdr.detectChanges();
  }

  CloseImageUploadModal(): void {
    this.showImageUploadModal = false;
    this.uploadedImageData = null;
    this.uploadedImageName = '';
  }

  OnImageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.globalService.presentToastMessage('Please select an image file', false);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.globalService.presentToastMessage('Image must be less than 5MB', false);
      return;
    }

    this.uploadedImageName = file.name;

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      this.ProcessUploadedImage(result);
    };
    reader.readAsDataURL(file);
  }

  ProcessUploadedImage(dataUrl: string): void {
    // Load the image to get its dimensions
    const img = new Image();
    img.onload = () => {
      // Store the original image data
      this.uploadedImageData = dataUrl;
      this.cdr.detectChanges();
    };
    img.src = dataUrl;
  }

  ClearUploadedImage(): void {
    this.uploadedImageData = null;
    this.uploadedImageName = '';
    // Reset the file input
    const fileInput = document.getElementById('imageUploadInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Text Input Methods
  OpenTextInputModal(): void {
    this.showTextInputModal = true;
    this.textInputValue = '';
    this.textInputFontSize = '14pt';
    this.textInputColor = '#000000';
    this.cdr.detectChanges();
  }

  CloseTextInputModal(): void {
    this.showTextInputModal = false;
    this.textInputValue = '';
  }

  AddTextAnnotation(): void {
    if (!this.textInputValue.trim() || !this.pdfDoc) {
      this.globalService.presentToastMessage('Please enter some text', false);
      return;
    }

    const currentPage = this.currentPage || 1;
    const pdfCanvas = this.pdfCanvases.toArray().find(
      (c: ElementRef) => parseInt(c.nativeElement.getAttribute('data-page') || '0') === currentPage
    );

    if (!pdfCanvas) return;

    const canvasWidth = pdfCanvas.nativeElement.width;
    const canvasHeight = pdfCanvas.nativeElement.height;

    // Calculate width based on text length and font size
    const fontSize = parseInt(this.textInputFontSize) || 14;
    const estimatedWidth = Math.max(100, this.textInputValue.length * fontSize * 0.6) * this.scale;
    const height = (fontSize + 10) * this.scale;

    const annotation: Annotation = {
      id: this.pdfViewerService.generateAnnotationId(),
      tool_name: 'text',
      x: (canvasWidth / 2) - (estimatedWidth / 2),
      y: (canvasHeight / 2) - (height / 2),
      width: estimatedWidth,
      height: height,
      page_num: currentPage,
      value: this.textInputValue,
      fontSize: this.textInputFontSize,
      backgroundColor: 'transparent',
      text_colour: this.textInputColor,
      rotation: 0
    };

    this.annotations.push(annotation);
    this.selectedAnnotation = annotation;
    this.RedrawAnnotations(currentPage);
    this.CloseTextInputModal();
  }

  AddImageAnnotation(): void {
    if (!this.uploadedImageData || !this.pdfDoc) {
      this.globalService.presentToastMessage('Please upload an image first', false);
      return;
    }

    const currentPage = this.currentPage || 1;
    const pdfCanvas = this.pdfCanvases.toArray().find(
      (c: ElementRef) => parseInt(c.nativeElement.getAttribute('data-page') || '0') === currentPage
    );

    if (!pdfCanvas) return;

    // Get image dimensions for proper aspect ratio
    const img = new Image();
    img.onload = () => {
      const canvasWidth = pdfCanvas.nativeElement.width;
      const canvasHeight = pdfCanvas.nativeElement.height;

      // Calculate dimensions maintaining aspect ratio
      // Default max size 150x100 pixels at scale
      let width = 150 * this.scale;
      let height = 100 * this.scale;

      const imgAspect = img.width / img.height;
      if (imgAspect > 1.5) {
        // Wide image
        height = width / imgAspect;
      } else {
        // Tall or square image
        width = height * imgAspect;
      }

      const annotation: Annotation = {
        id: this.pdfViewerService.generateAnnotationId(),
        tool_name: 'image',
        x: (canvasWidth / 2) - (width / 2),
        y: (canvasHeight / 2) - (height / 2),
        width: width,
        height: height,
        page_num: currentPage,
        value: this.uploadedImageName,
        fontSize: '10pt',
        backgroundColor: 'transparent',
        text_colour: '#000000',
        rotation: 0,
        imageData: this.uploadedImageData!
      };

      this.annotations.push(annotation);
      this.selectedAnnotation = annotation;
      this.RedrawAnnotations(currentPage);
      this.CloseImageUploadModal();
    };
    img.src = this.uploadedImageData;
  }
   private RescaleAnnotations(factor: number): void {
    this.annotations.forEach(a => {
      a.x *= factor;
      a.y *= factor;
      a.width *= factor;
      a.height *= factor;
    });
  }
  
}